// IMPORTANT: This must be the first import to set webpack publicPath before chunks are loaded
import './publicPath';

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter as Router } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/lib/integration/react'
import Loading from './components/loaders/Loading'
import { ToastContainer, toast } from 'react-toastify'

// CSS
import './assets/styles/index.css'
import './assets/styles/toast.css'
import { useSelector } from 'react-redux';

// const store = configureStore()
// const persistor = configurePersistor(store)

// Redux setup
const store= window.__REDUX_SHARED_STORE__;
const persistor= window.__REDUX_SHARED_PERSISTOR__;

// Expose toast function only if parent hasn't already exposed it
// This allows the stock app to work standalone, but use parent's toast when embedded
if (typeof window !== 'undefined' && !window.__PARENT_TOAST__) {
  window.__PARENT_TOAST__ = toast;
  setTimeout(() => {
    console.log('[Toast] Stock app exposed its toast (standalone mode)');
  }, 500);
} else if (typeof window !== 'undefined' && window.__PARENT_TOAST__) {
  setTimeout(() => {
    console.log('[Toast] Using parent app toast (embedded mode)');
  }, 500);
}

// const root = ReactDOM.createRoot(document.getElementById('root'));

// const {
//     REACT_APP_FCM_API_KEY,
//     REACT_APP_FCM_AUTH_DOMAIN,
//     REACT_APP_FCM_PROJECT_ID,
//     REACT_APP_FCM_STORAGE_BUCKET,
//     REACT_APP_FCM_MESSAGING_SENDER_ID,
//     REACT_APP_FCM_APP_ID,
//     REACT_APP_FCM_MEASUREMENT_ID,
//   } = process.env

// Used to build and pass env vars to sw for messaging
// const swUrl = `${process.env.PUBLIC_URL}/firebase-messaging-sw.js?REACT_APP_FCM_API_KEY=${REACT_APP_FCM_API_KEY}&REACT_APP_FCM_AUTH_DOMAIN=${REACT_APP_FCM_AUTH_DOMAIN}&REACT_APP_FCM_PROJECT_ID=${REACT_APP_FCM_PROJECT_ID}&REACT_APP_FCM_STORAGE_BUCKET=${REACT_APP_FCM_STORAGE_BUCKET}&REACT_APP_FCM_MESSAGING_SENDER_ID=${REACT_APP_FCM_MESSAGING_SENDER_ID}&REACT_APP_FCM_APP_ID=${REACT_APP_FCM_APP_ID}&REACT_APP_FCM_MEASUREMENT_ID=${REACT_APP_FCM_MEASUREMENT_ID}`;
// navigator.serviceWorker.register(swUrl);

const getEffectiveTheme = (themePreference) => {
  if (themePreference === 'dark') {
    return 'dark';
  }
  if (themePreference === 'system') {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  }
  return 'light';
};

const getDirection = (supportedLanguages, language) => {
  const index = supportedLanguages?.find(lang => lang.name.toLowerCase() === language?.toLowerCase());
  return index ? index.direction : "ltr";
};

const ToastContainerWrapper = () => {
    const supportedLanguages = useSelector((state) => state.translation.supportedLanguages);
    const language = useSelector((state) => state.translation.language);
    const themePreference = useSelector((state) => state.layout?.theme || 'light');

    // Check if parent toast is available (we're embedded)
    // Only render ToastContainer in standalone mode
    const isEmbedded = React.useMemo(() => {
        const embedded = !!window.__PARENT_TOAST__;
        return embedded;
    }, []);

    const [direction, setDirection] = React.useState("ltr");
    const [effectiveTheme, setEffectiveTheme] = React.useState(() => 
        getEffectiveTheme(themePreference)
    );

    React.useEffect(() => {
        setDirection(getDirection(supportedLanguages, language) || 'ltr');
    }, [language, supportedLanguages]);

    React.useEffect(() => {
        setEffectiveTheme(getEffectiveTheme(themePreference));
    }, [themePreference]);

    const isDarkMode = effectiveTheme === 'dark';

    React.useEffect(() => {
        const root = document.documentElement;
        
        // Colors matching the main app's theme (from theme/index.js)
        // Light theme uses background.default, dark theme uses background.paper
        const colors = {
            light: {
                background: '#f4f5f7', // background.default from light theme
                text: '#172b4d', // text.primary from light theme
                info: '#2196f3', // info.main
                success: '#4caf50', // success.main
                warning: '#ff9800', // warning.main
                error: '#ff0000', // error.main from light theme
                border: 'rgba(102, 102, 102, 0.2)' // divider from light theme
            },
            dark: {
                background: '#222b36', // background.paper from dark theme
                text: '#ffffff', // text.primary from dark theme
                info: '#2196f3', // info.main
                success: '#4caf50', // success.main
                warning: '#ff9800', // warning.main
                error: '#f44336', // error.main from dark theme
                border: 'rgba(145, 158, 171, 0.2)' // divider from dark theme
            }
        };

        const currentColors = isDarkMode ? colors.dark : colors.light;
        
        root.style.setProperty('--toastify-color-light', currentColors.background);
        root.style.setProperty('--toastify-color-dark', currentColors.background);
        root.style.setProperty('--toastify-text-color-light', currentColors.text);
        root.style.setProperty('--toastify-text-color-dark', currentColors.text);
        root.style.setProperty('--toastify-color-info', currentColors.info);
        root.style.setProperty('--toastify-color-success', currentColors.success);
        root.style.setProperty('--toastify-color-warning', currentColors.warning);
        root.style.setProperty('--toastify-color-error', currentColors.error);
        root.style.setProperty('--toastify-icon-color-info', currentColors.info);
        root.style.setProperty('--toastify-icon-color-success', currentColors.success);
        root.style.setProperty('--toastify-icon-color-warning', currentColors.warning);
        root.style.setProperty('--toastify-icon-color-error', currentColors.error);
        root.style.setProperty('--toastify-toast-background', currentColors.background);
        // Card hairline border for the compact toast look (see toast.css).
        root.style.setProperty('--toastify-toast-border', currentColors.border);
    }, [isDarkMode]);

    // Don't render ToastContainer if we're embedded (parent app handles toasts)
    if (isEmbedded) {
        return null;
    }
    
    return (
        <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={direction === 'rtl'}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={isDarkMode ? 'dark' : 'light'}
            className="admin-stock-toast-container"
        />
    );
};

class StockApp extends HTMLElement {
  connectedCallback() {
    // Re-attached (moved in the DOM) before the deferred unmount ran: keep the live root.
    if (this.unmountTimer) {
      clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }
    if (this.root) return;

    const basename = this.getAttribute('basename') || '';
    window.__ADMIN_STOCK_BASENAME__ = basename;

    const root = ReactDOM.createRoot(this);
    this.root = root;

    root.render(
      <Provider store={store}>
        <PersistGate loading={<Loading fullScreen />} persistor={persistor}>
          <Router basename={basename}>
            <App />
            <ToastContainerWrapper />
          </Router>
        </PersistGate>
      </Provider>
    );
  }

  // Leaving an admin stock page removes this element. Without unmounting, the detached tree keeps
  // re-rendering on the shell's shared store with its router frozen on the last stock route, and its
  // title effect resets document.title (e.g. "Stock Items" while on /operations/dashboard).
  // Deferred because this fires inside the shell's React commit, where a sync unmount is not allowed.
  disconnectedCallback() {
    this.unmountTimer = setTimeout(() => {
      this.unmountTimer = null;
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
    }, 0);
  }
}

// Only define the custom element if it hasn't been defined yet
// This prevents errors during hot module replacement in development
if (!customElements.get("admin-stock-app")) {
  customElements.define("admin-stock-app", StockApp);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
